import React, {useEffect, useRef} from 'react';
import {useIntl} from 'react-intl';
import {Modal, Text} from '@forecast-it/design-system';
import {closeModal as closeTopModal, MODAL_TYPE, showModal} from '../../../../shared/components/modals/generic_modal_conductor';
import {ModalProps} from '../../../../shared/components/modals/types';

export type DiscardTemplateChangesModalPayload = {
	onDiscard: () => void;
	onKeepEditing: () => void;
};

export const showDiscardTemplateChangesModal = (props: DiscardTemplateChangesModalPayload): void => {
	showModal({type: MODAL_TYPE.DISCARD_BASELINE_TEMPLATE_CHANGES, ...props});
};

/** Closes the topmost modal; for a caller that must dismiss this one because the navigation it guards is moot. */
export const closeDiscardTemplateChangesModal = (): void => {
	closeTopModal();
};

export const DiscardTemplateChangesModal = ({
	closeModal,
	onDiscard,
	onKeepEditing,
}: ModalProps<DiscardTemplateChangesModalPayload>): JSX.Element => {
	const {formatMessage} = useIntl();
	// Set once a button (or Escape) has answered, so the unmount fallback below does not answer twice.
	const answered = useRef(false);
	const latestKeepEditing = useRef(onKeepEditing);
	latestKeepEditing.current = onKeepEditing;
	// The modal can be removed without an answer, for example replaced by an error modal. The blocked
	// navigation is then dropped, exactly as if the user had chosen to keep editing.
	useEffect(
		() => () => {
			if (!answered.current) {
				latestKeepEditing.current();
			}
		},
		[]
	);
	const discard = () => {
		answered.current = true;
		onDiscard();
		closeModal();
	};
	// Closing by Escape or the close icon also means "keep editing": the blocked navigation is dropped.
	const keepEditing = () => {
		answered.current = true;
		onKeepEditing();
		closeModal();
	};
	return (
		<Modal size={Modal.SIZE.SMALL} onCloseModal={keepEditing} closeOnInteractOutside={false}>
			<Modal.Title>{formatMessage({id: 'baseline_templates.discard.title'})}</Modal.Title>
			<Modal.Content>
				<Text>{formatMessage({id: 'baseline_templates.discard.body'})}</Text>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton onPress={discard} dataCy="baseline-template-discard-confirm">
					{formatMessage({id: 'baseline_templates.discard.confirm'})}
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={keepEditing} dataCy="baseline-template-discard-keep-editing">
					{formatMessage({id: 'baseline_templates.discard.keep_editing'})}
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};
